/**
 * TCB R17 Quote Intelligence — adapts configurator/package/cart context into quote requests.
 * Extends existing /pages/request-a-quote — does not create a second quote page.
 */
(function (global) {
  'use strict';

  function buildFromConfigurator(session, customer) {
    var result = (session && session.result) || {};
    var context = (session && session.context) || {};
    var blocked = result.purchase_mode === 'blocked';
    var needsReview = result.purchase_mode !== 'direct_cart';
    return {
      quote_request_id: 'tcbqr_' + Date.now().toString(36),
      status: blocked ? 'blocked' : needsReview ? 'needs_review' : 'draft',
      source: { channel: 'configurator', source_id: session.session_id || null },
      customer: customer || { name: null, email: null, phone: null, company: null },
      facility: {
        facility_id: context.facility_id || null,
        legacy_facility_id: context.legacy_facility_id || null,
        zone_ids: context.zone_ids || []
      },
      groups: result.package_id
        ? [
            {
              group_id: 'g1',
              package_id: result.package_id,
              configuration_id: result.configuration_id || null,
              quantity: 1,
              unique_sku_count: result.unique_sku_count || 0,
              purchase_mode: result.purchase_mode || 'quote_preferred'
            }
          ]
        : [],
      validation: {
        all_groups_le8: (result.unique_sku_count || 0) <= 8,
        safety_complete: !result.eligibility || result.eligibility.wet_signage !== 'catalog_gap',
        compatibility_complete: !result.eligibility || result.eligibility.surface_gate !== 'blocked',
        pricing_ready: false
      },
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  }

  function buildFromPackage(pkg, customer) {
    var mode = (pkg.intelligence && pkg.intelligence.purchase_mode) || 'quote_preferred';
    return {
      quote_request_id: 'tcbqr_pkg_' + Date.now().toString(36),
      status: mode === 'blocked' ? 'blocked' : 'draft',
      source: { channel: 'package_page', source_id: pkg.id },
      customer: customer || { name: null, email: null, phone: null, company: null },
      facility: {
        facility_id: pkg.taxonomy && pkg.taxonomy.facility_taxonomy_id,
        legacy_facility_id: pkg.facility_id,
        zone_ids: (pkg.taxonomy && pkg.taxonomy.zone_ids) || []
      },
      groups: [
        {
          group_id: 'g1',
          package_id: pkg.id,
          configuration_id: pkg.default_configuration_id || null,
          quantity: 1,
          unique_sku_count: (pkg.intelligence && pkg.intelligence.sku_count) || 0,
          purchase_mode: mode
        }
      ],
      validation: {
        all_groups_le8: !pkg.intelligence || pkg.intelligence.sku_count <= 8,
        safety_complete: !pkg.intelligence || pkg.intelligence.wet_signage_fulfillment !== 'catalog_gap',
        compatibility_complete: !pkg.intelligence || pkg.intelligence.r12_surface_gate !== 'blocked',
        pricing_ready: false
      },
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  }

  /** R36: attach company/location/contact without a second quote engine. */
  function attachB2BContext(quote, b2bContext) {
    if (!quote) return quote;
    b2bContext = b2bContext || {};
    quote.b2b = {
      company_id: b2bContext.company_id || null,
      location_id: b2bContext.location_id || null,
      contact_id: b2bContext.contact_id || null,
      draft_order_adapter: !!b2bContext.draft_order_adapter,
      channel: b2bContext.channel || 'b2b_account_hub'
    };
    if (quote.customer && b2bContext.company_id) {
      quote.customer.company = quote.customer.company || b2bContext.company_id;
    }
    quote.updated_at = new Date().toISOString();
    return quote;
  }

  /**
   * R37: attach location tax readiness snapshot.
   * Never promises tax-free pricing while unverified. Does not create a second quote engine.
   */
  function attachTaxSnapshot(quote, taxContext) {
    if (!quote) return quote;
    taxContext = taxContext || {};
    var state = taxContext.tax_state || taxContext.state || 'UNKNOWN';
    var verified = state === 'EXEMPT_VERIFIED';
    quote.tax = {
      company_id: taxContext.company_id || (quote.b2b && quote.b2b.company_id) || null,
      company_location_id: taxContext.company_location_id || taxContext.location_id || (quote.b2b && quote.b2b.location_id) || null,
      tax_state: state,
      evidence_ids: taxContext.evidence_ids || [],
      review_required: taxContext.review_required != null ? !!taxContext.review_required : !verified && state !== 'TAXABLE',
      review_reason: taxContext.review_reason || null,
      tax_free_pricing_promised: false,
      commercially_valid: taxContext.commercially_valid !== false,
      snapshot_at: new Date().toISOString(),
      source: 'r37_tax_document_intelligence'
    };
    if (global.TCBTaxDocumentRuntime && typeof global.TCBTaxDocumentRuntime.quoteTaxReadiness === 'function' && quote.tax.company_id && quote.tax.company_location_id) {
      var readiness = global.TCBTaxDocumentRuntime.quoteTaxReadiness(
        quote.tax.company_id,
        quote.tax.company_location_id,
        quote.quote_request_id
      );
      quote.tax = Object.assign({}, quote.tax, readiness.snapshot, {
        tax_free_pricing_promised: false,
        ready_for_tax_exempt_conversion: !!readiness.ready_for_tax_exempt_conversion
      });
    }
    quote.updated_at = new Date().toISOString();
    return quote;
  }

  function toQuery(quote) {
    var params = new URLSearchParams();
    params.set('topic', quote.b2b && quote.b2b.company_id ? 'b2b' : 'equipment-package');
    params.set('quote_request_id', quote.quote_request_id || '');
    params.set('source', (quote.source && quote.source.channel) || 'package_page');
    if (quote.groups && quote.groups[0]) {
      params.set('package', quote.groups[0].package_id || '');
      params.set('mode', quote.groups[0].purchase_mode || '');
    }
    if (quote.facility && quote.facility.legacy_facility_id) {
      params.set('facility', quote.facility.legacy_facility_id);
    }
    if (quote.b2b) {
      if (quote.b2b.company_id) params.set('company_id', quote.b2b.company_id);
      if (quote.b2b.location_id) params.set('location_id', quote.b2b.location_id);
      if (quote.b2b.contact_id) params.set('contact_id', quote.b2b.contact_id);
    }
    return params.toString();
  }

  function quoteUrl(quote, base) {
    var root = base || '/pages/request-a-quote';
    return root + (root.indexOf('?') === -1 ? '?' : '&') + toQuery(quote);
  }

  function hydrateFormFromUrl(form) {
    if (!form || !global.location) return null;
    var params = new URLSearchParams(global.location.search || '');
    var packageId = params.get('package');
    var facility = params.get('facility');
    var quoteRequestId = params.get('quote_request_id');
    var source = params.get('source');
    if (!packageId && !quoteRequestId) return null;
    var note = form.querySelector('[name="body"], textarea, [name="message"], [name="comment"]');
    if (note && !note.value) {
      note.value =
        'TCB Quote Intelligence context\n' +
        'quote_request_id: ' +
        (quoteRequestId || '(new)') +
        '\n' +
        'source: ' +
        (source || 'unknown') +
        '\n' +
        'package: ' +
        (packageId || '') +
        '\n' +
        'facility: ' +
        (facility || '') +
        '\n' +
        'mode: ' +
        (params.get('mode') || '') +
        '\n' +
        'company_id: ' +
        (params.get('company_id') || '') +
        '\n' +
        'location_id: ' +
        (params.get('location_id') || '') +
        '\n' +
        'contact_id: ' +
        (params.get('contact_id') || '') +
        '\n';
    }
    ['package', 'facility', 'quote_request_id', 'source', 'mode', 'company_id', 'location_id', 'contact_id'].forEach(function (key) {
      var el = form.querySelector('[name="' + key + '"]');
      if (el && params.get(key)) el.value = params.get(key);
    });
    return {
      quote_request_id: quoteRequestId,
      package_id: packageId,
      facility: facility,
      source: source,
      company_id: params.get('company_id'),
      location_id: params.get('location_id'),
      contact_id: params.get('contact_id')
    };
  }

  function boot() {
    document.querySelectorAll('form[action*="contact"], form[data-tcb-quote-form], #tcb-contact-form, form.tcb-eta-quote-form').forEach(function (form) {
      hydrateFormFromUrl(form);
    });
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }

  global.TCBQuoteIntelligence = {
    buildFromConfigurator: buildFromConfigurator,
    buildFromPackage: buildFromPackage,
    attachB2BContext: attachB2BContext,
    attachTaxSnapshot: attachTaxSnapshot,
    quoteUrl: quoteUrl,
    hydrateFormFromUrl: hydrateFormFromUrl
  };
})(typeof window !== 'undefined' ? window : globalThis);
